export const rowProduct = (saleProduct) => saleProduct?.Product || saleProduct?.product;

/** Точний збіг: штрихкод, артикул, код */
export const productMatchesCodeExact = (product, rawCode) => {
  if (!product || rawCode == null) return false;
  const code = String(rawCode).trim();
  if (!code) return false;
  const low = code.toLowerCase();
  const bc = String(product.barcode || '').trim();
  if (bc && bc === code) return true;
  const art = String(product.article || '').trim().toLowerCase();
  if (art && art === low) return true;
  const cde = String(product.code || '').trim().toLowerCase();
  if (cde && cde === low) return true;
  return false;
};

/** Частковий збіг за артикулом, кодом або штрихкодом (мін. 2 символи) */
export const productMatchesPartialArticle = (product, rawCode) => {
  if (!product || rawCode == null) return false;
  const q = String(rawCode).trim().toLowerCase();
  if (q.length < 2) return false;
  const art = String(product.article || '').trim().toLowerCase();
  if (art && art.includes(q)) return true;
  const cde = String(product.code || '').trim().toLowerCase();
  if (cde && cde.includes(q)) return true;
  const bc = String(product.barcode || '').trim().toLowerCase();
  if (bc && bc.includes(q)) return true;
  return false;
};

const rowMatchKey = (row) => row.saleProduct?.id ?? row.pickLine?.id;

/**
 * Рядки документа, що відповідають коду.
 * @param {boolean} allowPartialArticle — для ручного вводу з клавіатури
 */
export const findRowsForScanCode = (mergedRows, rawCode, { allowPartialArticle = false } = {}) => {
  const code = String(rawCode || '').trim();
  if (!code) return [];

  const exact = [];
  const partial = [];

  mergedRows.forEach((row) => {
    if (row.isOrphan) return;
    const p = rowProduct(row.saleProduct);
    if (!p) return;
    if (productMatchesCodeExact(p, code)) {
      exact.push(row);
      return;
    }
    if (allowPartialArticle && productMatchesPartialArticle(p, code)) {
      partial.push(row);
    }
  });

  if (allowPartialArticle) {
    const seen = new Set();
    const merged = [];
    [...exact, ...partial].forEach((row) => {
      const key = rowMatchKey(row);
      if (key == null || seen.has(key)) return;
      seen.add(key);
      merged.push(row);
    });
    return merged;
  }

  if (exact.length) return exact;
  return partial;
};

export const rowIsOpenForPicking = (row) => {
  if (row.isOrphan || !row.saleProduct) return false;
  const doc = Number(row.saleProduct.quantity);
  const fact = Number(row.pickLine?.quantity ?? 0);
  return fact < doc - 0.0001;
};

/** Факт дорівнює або перевищує кількість по документу */
export const rowIsFullyCollected = (row) => {
  if (row.isOrphan || !row.saleProduct) return false;
  return !rowIsOpenForPicking(row);
};

/** Відкриті рядки зверху, далі за порядком у документі */
export const sortPickCandidateRows = (rows) =>
  [...rows].sort((a, b) => {
    const aOpen = rowIsOpenForPicking(a) ? 0 : 1;
    const bOpen = rowIsOpenForPicking(b) ? 0 : 1;
    if (aOpen !== bOpen) return aOpen - bOpen;
    return (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999);
  });

export const formatRowProductTitle = (row) => {
  const p = row.isOrphan
    ? row.productFallback || row.pickLine?.Product
    : rowProduct(row.saleProduct);
  const name = p ? `${p.name || ''} ${p.modName || ''}`.trim() : '—';
  const article = p?.article != null && String(p.article).trim() !== '' ? String(p.article).trim() : '—';
  return { name: name || '—', article };
};

export const rowToScanErrorProduct = (row) => {
  const { name, article } = formatRowProductTitle(row);
  const doc = row.saleProduct ? Number(row.saleProduct.quantity) : undefined;
  const fact = row.pickLine ? Number(row.pickLine.quantity ?? 0) : undefined;
  return { name, article, doc, fact };
};

/** Рядки документа з частковим збігом (ручний пошук) */
export const findPartialDocumentRows = (mergedRows, rawCode) => {
  const code = String(rawCode || '').trim();
  if (code.length < 2) return [];
  return mergedRows.filter((row) => {
    if (row.isOrphan) return false;
    const p = rowProduct(row.saleProduct);
    return Boolean(p && productMatchesPartialArticle(p, code));
  });
};

/** Частковий ввід — показувати список навіть якщо знайдено один рядок */
export const shouldShowManualPickList = (code, candidates) => {
  if (candidates.length > 1) return true;
  if (candidates.length !== 1) return false;
  const p = rowProduct(candidates[0].saleProduct);
  if (!p) return false;
  return (
    productMatchesPartialArticle(p, code) && !productMatchesCodeExact(p, code)
  );
};

export const catalogProductToScanError = (product) => {
  if (!product) return { name: '—', article: '—' };
  const name = `${product.name || ''} ${product.modName || ''}`.trim() || '—';
  const article =
    product.article != null && String(product.article).trim() !== ''
      ? String(product.article).trim()
      : '—';
  return { name, article };
};
